<template>
	<div class="relative isolate overflow-visible w-full max-w-container mx-auto -mb-16 lg:mb-0">
		<Motion
			as-child
			:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
			:initial="{ opacity: 0, transform: 'translateY(10px)' }"
			:transition="{ duration: 1, delay: 0.1 }"
			:in-view-options="{ once: true }"
			class="will-change-transform"
		>
			<div
				class="min-h-32 block md:hidden prose prose-h1:text-5xl group prose-h1:[&_span]:first-of-type:semibold prose-h1:[&_span]:last-of-type:font-semibold md:prose-h1:text-5xl prose-h1:tracking-tight prose-h1:font-semibold prose-h2:text-4xl prose-h2:text-pretty prose-h2:tracking-tight prose-h2:font-semibold prose-h2:text-highlighted prose-h2:mt-0 prose-h1:mb-0 px-4 pt-2"
				v-html="renderRichText(((text?.title || '') as unknown as StoryblokRichTextNode<string>))"
			/>
		</Motion>
		<UPageHero
			reverse
			orientation="horizontal"
			:ui="{
				description: 'prose block prose-p:text-lg sm:prose-p:text-xl/8 prose-p:text-muted prose-p:font-medium'
			}"
		>
			<template #title>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(10px)' }"
					:transition="{ duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					<span
						class="hidden md:block prose prose-h1:text-4xl group prose-h1:[&_span]:first-of-type:semibold prose-h1:[&_span]:last-of-type:font-semibold lg:prose-h1:[&_span]:first-of-type:whitespace-nowrap sm:prose-h1:text-7xl prose-h1:tracking-tight prose-h1:font-semibold prose-h2:text-4xl sm:prose-h2:text-6xl prose-h2:text-pretty prose-h2:tracking-tight prose-h2:font-semibold prose-h2:text-highlighted prose-h2:mt-0 prose-h1:mb-0"
						:class="[areaName ? 'mt-8' : 'lg:-mt-16']"
						v-html="renderRichText(((text?.title || '') as unknown as StoryblokRichTextNode<string>))"
					/>
				</Motion>
			</template>
			<template #description>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					<p
						v-if="text?.subtitle"
						class=""
						v-html="renderRichText(((text?.subtitle || '') as unknown as StoryblokRichTextNode<string>))"
					/>
				</Motion>
			</template>
			<template #links>
				<Motion
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateY(0px)' }"
					:initial="{ opacity: 0, transform: 'translateY(25px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.1 }"
					:in-view-options="{ once: true }"
					class="will-change-transform"
				>
					<SignupAddressChecker
						:tracking-location="trackingLocation"
						compact
						class="w-full lg:-mt-12"
					/>
				</Motion>
				<Motion
					v-if="areaName"
					as-child
					:while-in-view="{ opacity: 1, transform: 'translateX(0px)' }"
					:initial="{ opacity: 0, transform: 'translateX(50px)' }"
					:transition="{ type: 'spring', bounce: 0.5, duration: 1, delay: 0.7 }"
					:in-view-options="{ once: true }"
				>
					<div class="text-lg sm:text-xl/8 text-muted text-pretty mt-6">
						<p>
							<span v-html="$t('service-area.hero.p1', { areaName })" /><br><br>
							{{ $t('service-area.hero.p2', { areaCity }) }}<br><br>
						</p>
						<ul class="list-disc list-inside ml-2">
							<li
								v-for="item in $tm('service-area.hero.list-items')"
								:key="item"
							>
								{{ $rt(item, { areaCity }) }}
							</li>
						</ul>
					</div>
				</Motion>
			</template>
			<div class="max-w-[220px] sm:max-w-[320px] lg:max-w-[380px] xl:max-w-[405px] -mt-2 w-full lg:flex lg:items-end lg:justify-end lg:ml-auto">
				<SvgImage
					:id="`svg-image-${svg.id}`"
					provider="storyblok"
					preload
					fetchpriority="high"
					sizes="220px sm:320px lg:380px xl:405px"
					:image="svg.image"
					:lines="svg.lines"
					:circles="svg.circles"
					:viewbox="svg.viewbox"
					rounded
					class="h-full w-full lg:min-h-[350px] pb-4 xl:pb-24 lg:pb-0 -mt-8 lg:-mb-12 lg:mt-16 xl:mt-16 sm:mr-8 lg:mr-4 xl:mr-9 xl:-mb-24 shrink-0 -z-10"
				/>
			</div>
		</UPageHero>
	</div>
</template>

<script setup lang="ts">
import type { SvgImageProps } from '@/types/components/svg-image'
import type { HeroMarkdown } from '@/types/storyblok/hero'
import type { StoryblokRichTextNode } from '@storyblok/vue'
import { Motion } from 'motion-v'

const props = defineProps({
	image: {
		type: Object as PropType<SvgImageProps['image']>,
		required: true
	},
	text: {
		type: Object as PropType<HeroMarkdown>,
		required: true
	},
	areaName: {
		type: String,
		default: ''
	},
	trackingLocation: {
		type: String,
		required: true
	}
})

const {
	image,
	text,
	areaName
} = toRefs(props)

const areaCity = computed(() => {
	return areaName.value.split(',')[0]
})

const svg: SvgImageProps = {
	id: 'service-areas-main-hero-aside',
	viewbox: {
		width: 405,
		height: 405
	},
	image: {
		url: image.value?.url ?? '/home_content_aside_4.webp',
		alt: image.value?.alt ?? 'Maverix Fiber-fast internet',
		densities: 'x1 x2'
	},
	lines: [
		{
			rotation: -15,
			length: 150,
			direction: 'counter-clockwise',
			gap: 40,
			stroke: '#FF5733'
		}
	],
	circles: [
		{
			cx: 51.8,
			cy: 76,
			r: 85,
			fill: '#01629E'
		},
		{
			cx: 376,
			cy: 100,
			r: 21,
			fill: '#FF8A02'
		}
	]
}
</script>
